import React, { useState, useEffect, useMemo } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  Background,
  Node,
  Edge,
  MarkerType,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  Position,
  Handle,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Card, CardContent, Typography, Box, Chip, Divider, Button, useTheme, Tooltip } from '@mui/material';
import { FoodRoute } from '../../types';

interface FoodFlowDiagramProps {
  route: FoodRoute;
  onShowDetail?: () => void;
}

const CustomNode: React.FC<{
  data: { label: string; nodeType: 'feedstock' | 'process' | 'product' };
  isDarkMode: boolean;
  isActive?: boolean;
}> = ({ data, isDarkMode, isActive }) => {
  const getNodeStyle = () => {
    const baseStyle = {
      padding: '12px 16px',
      borderRadius: '8px',
      fontSize: '13px',
      fontWeight: 500,
      minWidth: '160px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      position: 'relative' as const,
    };

    switch (data.nodeType) {
      case 'feedstock':
        return {
          ...baseStyle,
          background: isDarkMode ? '#1e3a5f' : '#e3f2fd',
          border: `2px solid ${isDarkMode ? '#4fc3f7' : '#2196f3'}`,
          color: isDarkMode ? '#ffffff' : '#000000',
        };
      case 'process':
        return {
          ...baseStyle,
          background: isActive
            ? (isDarkMode ? '#4a3728' : '#fff3e0')
            : (isDarkMode ? '#2a2a2a' : '#f5f5f5'),
          border: isActive
            ? `3px solid ${isDarkMode ? '#ffb74d' : '#ff9800'}`
            : `2px solid ${isDarkMode ? '#666666' : '#9e9e9e'}`,
          color: isDarkMode ? '#ffffff' : '#000000',
          cursor: 'pointer',
        };
      case 'product':
        return {
          ...baseStyle,
          background: isDarkMode ? '#1b5e20' : '#e8f5e9',
          border: `2px solid ${isDarkMode ? '#66bb6a' : '#4caf50'}`,
          color: isDarkMode ? '#ffffff' : '#000000',
        };
      default:
        return baseStyle;
    }
  };

  return (
    <div style={getNodeStyle()}>
      {data.nodeType === 'feedstock' && <Handle type="source" position={Position.Right} />}
      {data.nodeType === 'process' && (
        <>
          <Handle type="target" position={Position.Left} />
          <Handle type="source" position={Position.Right} />
        </>
      )}
      {data.nodeType === 'product' && <Handle type="target" position={Position.Left} />}
      
      <span style={{ flex: 1, textAlign: 'center' }}>{data.label}</span>
    </div>
  );
};

const FoodFlowDiagramInner: React.FC<FoodFlowDiagramProps> = ({ route, onShowDetail }) => {
  const [activeStep, setActiveStep] = useState<number | null>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [expanded, setExpanded] = useState(false);
  const isLongFlow = route.processSteps.length > 5;

  const rfInstance = useReactFlow();

  useEffect(() => {
    const timer = setTimeout(() => {
      rfInstance.fitView({ padding: 0.15, duration: 300 });
    }, 60);
    return () => clearTimeout(timer);
  }, [route, rfInstance, expanded]);

  useEffect(() => {
    setExpanded(false);
  }, [route.id]);

  const nodeTypes = useMemo(() => ({
    custom: (props: any) => (
      <CustomNode
        {...props}
        isDarkMode={isDarkMode}
        isActive={activeStep !== null && props.id === `step-${activeStep}`}
      />
    ),
  }), [isDarkMode, activeStep]);

  useEffect(() => {
    
    const verticalSpacing = 90;
    const horizontalSpacing = 240;
    const startX = 0;
    const centerY = 150;

    const feedstockNodes: Node[] = route.feedstock.map((item, index) => {
      const yOffset = (index - (route.feedstock.length - 1) / 2) * verticalSpacing;
      return {
        id: `feedstock-${index}`,
        type: 'custom',
        position: { x: startX, y: centerY + yOffset },
        data: {
          label: item,
          nodeType: 'feedstock' as const,
        },
      };
    });

    const processNodes: Node[] = route.processSteps.map((step, index) => ({
      id: `step-${index}`,
      type: 'custom',
      position: { x: startX + (index + 1) * horizontalSpacing, y: centerY },
      data: {
        label: step,
        nodeType: 'process' as const,
      },
    }));

    const productNodes: Node[] = route.products.map((item, index) => {
      const yOffset = (index - (route.products.length - 1) / 2) * verticalSpacing;
      return {
        id: `product-${index}`,
        type: 'custom',
        position: { x: startX + (route.processSteps.length + 1) * horizontalSpacing, y: centerY + yOffset },
        data: {
          label: item,
          nodeType: 'product' as const,
        },
      };
    });

    setNodes([...feedstockNodes, ...processNodes, ...productNodes]);

    const edgeList: Edge[] = [];

    route.feedstock.forEach((_, index) => {
      edgeList.push({
        id: `feedstock-${index}-step-0`,
        source: `feedstock-${index}`,
        target: 'step-0',
        animated: true,
        style: { stroke: isDarkMode ? '#4fc3f7' : '#2196f3', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: isDarkMode ? '#4fc3f7' : '#2196f3' },
      });
    });

    route.processSteps.forEach((_, index) => {
      if (index < route.processSteps.length - 1) {
        edgeList.push({
          id: `step-${index}-step-${index + 1}`,
          source: `step-${index}`,
          target: `step-${index + 1}`,
          animated: true,
          style: { stroke: isDarkMode ? '#666666' : '#9e9e9e', strokeWidth: 2 },
          markerEnd: { type: MarkerType.ArrowClosed, color: isDarkMode ? '#666666' : '#9e9e9e' },
        });
      }
    });

    route.products.forEach((_, index) => {
      edgeList.push({
        id: `step-last-product-${index}`,
        source: `step-${route.processSteps.length - 1}`,
        target: `product-${index}`,
        animated: true,
        style: { stroke: isDarkMode ? '#66bb6a' : '#4caf50', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: isDarkMode ? '#66bb6a' : '#4caf50' },
      });
    });

    setEdges(edgeList);
  }, [route, activeStep, setNodes, setEdges, isDarkMode]);

  const onNodeClick = (_event: React.MouseEvent, node: Node) => {
    if (node.id.startsWith('step-')) {
      const stepIndex = parseInt(node.id.split('-')[1], 10);
      setActiveStep(activeStep === stepIndex ? null : stepIndex);
    }
  };

  return (
    <Card sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
    }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography
            variant="h5"
            sx={{ color: isDarkMode ? '#66bb6a' : '#2e7d32', fontWeight: 600 }}
          >
            {route.name} 工艺流程
          </Typography>
          {onShowDetail && (
            <Tooltip title="查看工艺详情">
              <Button onClick={onShowDetail} size="small" sx={{ minWidth: 0, px: 1.5 }}>详情</Button>
            </Tooltip>
          )}
        </Box>

        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">原料</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
              {route.feedstock.map((item) => (
                <Chip key={item} label={item} size="small" color="primary" variant="outlined" />
              ))}
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">产品</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
              {route.products.map((item) => (
                <Chip key={item} label={item} size="small" color="success" variant="outlined" />
              ))}
            </Box>
          </Box>
        </Box>

        <Divider sx={{ mb: 2, borderColor: isDarkMode ? '#444444' : '#e0e0e0' }} />

        {isLongFlow && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              流程较长，已折叠为概览显示
            </Typography>
            <Button size="small" variant="outlined" onClick={() => setExpanded(!expanded)}>
              {expanded ? '收起' : '展开大图'}
            </Button>
          </Box>
        )}
        <Box sx={{
          height: isLongFlow ? (expanded ? 620 : 360) : 340,
          border: '1px solid',
          borderColor: isDarkMode ? '#444444' : '#e0e0e0',
          borderRadius: 2
        }}>
          <ReactFlow
            key={route.id}
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            fitView
            minZoom={0.1}
            attributionPosition="bottom-left"
          >
            <Background
              variant={BackgroundVariant.Dots}
              gap={12}
              size={1}
              color={isDarkMode ? '#444444' : '#e0e0e0'}
            />
          </ReactFlow>
        </Box>

        {activeStep !== null && (
          <Box sx={{
            mt: 2,
            p: 2,
            backgroundColor: isDarkMode ? '#4a3728' : '#fff3e0',
            borderRadius: 2
          }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: isDarkMode ? '#ffb74d' : '#ff9800'
              }}
            >
              当前步骤: {route.processSteps[activeStep]}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              步骤 {activeStep + 1} / {route.processSteps.length}
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              发酵类型:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isDarkMode ? '#66bb6a' : '#2e7d32',
                fontWeight: 600
              }}
            >
              {route.fermentationType}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              生产周期:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isDarkMode ? '#81d4fa' : '#0288d1',
                fontWeight: 600
              }}
            >
              {route.productionCycle}
            </Typography>
          </Box>
          {route.temperature && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                温度:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: isDarkMode ? '#ffb74d' : '#ff9800',
                  fontWeight: 600
                }}
              >
                {route.temperature}
              </Typography>
            </Box>
          )}
        </Box>

        {route.keyMicroorganisms && route.keyMicroorganisms.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              关键微生物:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {route.keyMicroorganisms.map((item, index) => (
                <Chip
                  key={index}
                  label={item}
                  size="small"
                  sx={{
                    backgroundColor: isDarkMode ? '#4a148c' : '#f3e5f5',
                    color: isDarkMode ? '#e1bee7' : '#7b1fa2',
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {route.advantages && route.advantages.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              技术优势:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {route.advantages.map((adv, index) => (
                <Chip
                  key={index}
                  label={adv}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        {route.challenges && route.challenges.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              技术挑战:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {route.challenges.map((chal, index) => (
                <Chip
                  key={index}
                  label={chal}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const FoodFlowDiagram: React.FC<FoodFlowDiagramProps> = (props) => (
  <ReactFlowProvider>
    <FoodFlowDiagramInner {...props} />
  </ReactFlowProvider>
);

export default FoodFlowDiagram;
